import { useLocation } from 'preact-iso'
import { RefreshCcw, AlertCircle, Send, Loader2 } from 'lucide-preact'
import { useEmailBlasts } from '@/hooks/useEmailBlasts'
import type { EmailBlastViewDto } from '@/types/emailBlast'
import { isTerminalBlastState } from '@/types/emailBlast'
import type { WebViewDto } from '@/api/webs'
import { routes } from '@/utils/routes'
import { BlastStateBadge } from '../email-blasts/BlastStateBadge'
import { formatRate } from '../email-blasts/engagementFormat'

interface Props {
  web: WebViewDto
  clientCompanyId: string
  workspaceId: string
  campaignId: string
}

const OPEN_RATE_CAVEAT = 'Inflated by inbox privacy; clicks are the reliable signal.'

/**
 * Broadcasts monitor (ORB-348/350/384) for this web's email activations. A dense, scannable list of
 * blasts — state, delivered, click/open rates, breaker — with inline Cancel/Resume on active rows and a
 * link into each blast's deep-linkable detail. Clicks are the privileged signal; open rate is caveated.
 */
export function WebEmailBlastsTab({ web, clientCompanyId, workspaceId, campaignId }: Props) {
  const location = useLocation()
  const { blasts, loading, error, actioningIds, load, resume, cancel } = useEmailBlasts({
    workspaceId,
    campaignId,
    webId: web.Id,
  })

  const handleResume = async (blast: EmailBlastViewDto) => {
    const result = await resume(blast.Id)
    if (!result.success) await load() // refresh on a 409 too — local state is stale if it wasn't Paused
  }

  const openBlast = (blast: EmailBlastViewDto) => {
    location.route(routes.scoped(clientCompanyId, workspaceId).webBroadcast(campaignId, web.Id, blast.Id))
  }

  const hasBlasts = !!(blasts && blasts.length > 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Broadcasts</h2>
          <p className="text-sm mt-1 text-slate-600 dark:text-slate-400">
            Monitor each activation's controlled rollout; open a blast to pause, resume, or cancel.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors disabled:opacity-50"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Refreshing' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {loading && !blasts && (
        <div className="h-48 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 animate-pulse" />
      )}

      {!loading && !hasBlasts && !error && (
        <div className="p-8 border border-dashed rounded-xl border-slate-300 dark:border-slate-600 text-center">
          <Send className="w-8 h-8 mx-auto text-slate-400" />
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            No broadcasts yet. A blast appears here once an email activation begins sending.
          </p>
        </div>
      )}

      {hasBlasts && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500">
                <th className="px-5 py-2.5 font-semibold">Blast</th>
                <th className="px-3 py-2.5 font-semibold">State</th>
                <th className="px-3 py-2.5 font-semibold text-right">Delivered</th>
                <th className="px-3 py-2.5 font-semibold text-right text-primary-700/70 dark:text-primary-400/70">Click %</th>
                <th className="px-3 py-2.5 font-semibold text-right">
                  <span className="underline decoration-dotted underline-offset-2 cursor-help" title={OPEN_RATE_CAVEAT}>Open % ⓘ</span>
                </th>
                <th className="px-3 py-2.5 font-semibold text-right">Breaker</th>
                <th className="px-5 py-2.5 font-semibold text-right" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800/70">
              {blasts!.map(blast => (
                <BlastRow
                  key={blast.Id}
                  blast={blast}
                  actioning={actioningIds.has(blast.Id)}
                  onOpen={() => openBlast(blast)}
                  onResume={() => handleResume(blast)}
                  onCancel={() => cancel(blast.Id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

interface BlastRowProps {
  blast: EmailBlastViewDto
  actioning: boolean
  onOpen: () => void
  onResume: () => void
  onCancel: () => void
}

function BlastRow({ blast, actioning, onOpen, onResume, onCancel }: BlastRowProps) {
  const terminal = isTerminalBlastState(blast.State)
  const paused = blast.State === 'Paused'
  const engagement = blast.Engagement
  const hasEngagement = !!engagement && engagement.Delivered > 0
  const bounceOver = blast.MaxHardBouncePercent > 0 && blast.Stats.HardBouncePercent >= blast.MaxHardBouncePercent

  return (
    <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
      <td className="px-5 py-3 cursor-pointer" onClick={onOpen}>
        <span className="font-medium text-slate-900 dark:text-white">{formatSendDate(blast)}</span>
        <span className="block text-xs text-slate-400 dark:text-slate-500">{(blast.TotalAudience - blast.PulledAfterRegistrationCount).toLocaleString()} recipients</span>
      </td>
      <td className="px-3 py-3"><BlastStateBadge state={blast.State} /></td>
      <td className="px-3 py-3 text-right tabular-nums text-slate-600 dark:text-slate-300">{blast.Stats.Delivered.toLocaleString()}</td>
      <td className="px-3 py-3 text-right tabular-nums font-medium text-primary-700 dark:text-primary-400">
        {hasEngagement ? formatRate(engagement!.ClickRatePercent) : <span className="text-slate-400 dark:text-slate-500" title="Too early to measure">—</span>}
      </td>
      <td className="px-3 py-3 text-right tabular-nums text-slate-400 dark:text-slate-500">
        {hasEngagement ? formatRate(engagement!.OpenRatePercent) : '—'}
      </td>
      <td className="px-3 py-3 text-right tabular-nums">
        <span className={bounceOver ? 'font-medium text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}>
          {blast.Stats.HardBouncePercent.toFixed(1)}
          <span className={bounceOver ? 'text-red-400' : 'text-slate-400 dark:text-slate-600'}>/{blast.MaxHardBouncePercent}%</span>
        </span>
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {actioning && <Loader2 className="inline w-3.5 h-3.5 animate-spin text-slate-400 mr-2" />}
        {paused && (
          <>
            <button onClick={onResume} disabled={actioning} className="text-xs font-medium text-primary-700 dark:text-primary-400 hover:underline disabled:opacity-50">Resume</button>
            <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
          </>
        )}
        {!terminal && (
          <>
            <button onClick={onCancel} disabled={actioning} className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50">Cancel</button>
            <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
          </>
        )}
        <button onClick={onOpen} className="text-xs font-medium text-primary-700 dark:text-primary-400 hover:underline">Manage →</button>
      </td>
    </tr>
  )
}

// No blast name exists on the DTO, so the send date is the row's human identifier (with the audience
// size beneath it). Scheduled blasts read "Scheduled <date>"; sent ones read the date alone.
function formatSendDate(blast: EmailBlastViewDto): string {
  const iso = blast.SendAtAbsoluteUtc || blast.CreatedDate
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'Broadcast'
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
  return blast.State === 'Canary' || blast.State === 'Measuring' ? `Scheduled ${date}` : date
}
